import { IconDefaultType as IconType } from "@slhuiicon/icon";
import { LitElement, TemplateResult, html } from "lit";
import { customElement } from "lit/decorators.js";
import { SlhCoreModal } from "@slhuicore/modal";
import { FocusTrappable } from "@slhui/base";
import { Translatable } from "@slhui/i18n/src/mixins/translatable";
import { style } from "./assets/modal.generated-style";
import { MODAL_ICON_HEIGHT, MODAL_ICON_WIDTH } from "./modal.constants";

/**
 * @CustomElement slh-modal
 *
 * @cssprop --slh-default-font-family - Font family.
 * @cssprop --slh-base-xl-font-size - Title font size.
 * @cssprop --slh-base-l-line-height - Title line height.
 * @cssprop --slh-base-semi-bold-font-weight - Paragraph font weight.
 * @cssprop --slh-base-bold-font-weight - Title font weight.
 * @cssprop --slh-color-neutral - Title color.
 * @cssprop --slh-color-neutral-shade-00 - Background coloror.
 * @cssprop --slh-color-neutral-shade-20 - Icon color.
 * @cssprop --slh-color-neutral-shade-40 - Close button color. Scrollbar color.
 * @cssprop --slh-color-neutral-shade-80 - Paragraph font color.
 * @cssprop --slh-color-neutral-shade-80-opacity-90 - Modal overlay color.
 * @cssprop --slh-color-neutral - Title font color.
 * @cssprop --slh-color-primary - Icon color for modal type "action".
 * @cssprop --slh-color-error - Icon color for modal type "alert".
 *
 * @slot icon - Custom content on the top of the modal (designed for icon)
 * @slot title - Contents for modal headline. Assigned slot nodes are parsed for non-empty Node.textContent, which is set as modal headline, while slot itself is hidden.
 * @slot content - Modal content
 * @slot buttons - Contents for modal buttons. Assigned slot nodes are parsed so that only slh-button components are shown.
 */
@customElement("slh-modal")
export default class SlhModal extends Translatable(
  FocusTrappable(SlhCoreModal(LitElement))
) {
  static styles = style;

  // eslint-disable-next-line class-methods-use-this
  private getCloseIcon(): string {
    return IconType.ICO_CLOSE;
  }

  /**
   *
   * @returns {TemplateResult} render Close button
   */
  private renderCloseIcon(): TemplateResult {
    return html`<slh-icon
      class="close-button"
      type="${this.getCloseIcon()}"
      width="${MODAL_ICON_WIDTH}"
      height="${MODAL_ICON_HEIGHT}"
      focusable="true"
      @click="${this.closeModal}"
      aria-label="${this.trans("modal.close")}"
    ></slh-icon>`;
  }
}
